import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, unwrap } from '@/api/client';
import { describeError } from '@/api/errors';
import { useOrgId } from '@/app/session';
import { useToast } from '@/components/ui';
import { formatClock } from '@/lib/format';
import { conversationListQuery, type Conversation } from './api';
import { cancelSend, draftInvalidations } from './drafts';

/** How long the in-place confirmation stays before moving on. */
export const ADVANCE_MS = 1200;

export type Handled = { kind: 'approved'; draftId: string; sendAt: string } | { kind: 'discarded' };

interface Flow {
  /** Conversations that need you, in list order (undefined while loading). */
  needs: Conversation[] | undefined;
  /** Replies approved or discarded in this session, for the progress line. */
  handled: number;
  /** Ids that need you right now, captured when a reply is handled. */
  snapshot: () => string[];
  /** Records a handled reply. */
  record: (h: Handled) => void;
  /** Opens the next conversation that needs you after `fromId`, or the done state. */
  advance: (fromId: string, snapshot: string[], handled?: Handled) => void;
  /** Withdraws an approval while the send hold runs; the reply is back for review. */
  undo: (draftId: string, conversationId: string) => Promise<boolean>;
}

const FlowContext = createContext<Flow | null>(null);

export function useFlow(): Flow {
  const f = useContext(FlowContext);
  if (!f) throw new Error('useFlow outside ConversationsLayout');
  return f;
}

/**
 * The review session: approve or discard a proposed reply, see it confirmed in place, move on to the
 * next conversation that needs you. Undo stays reachable from the toast after moving on.
 */
export function FlowProvider({ children }: { children: ReactNode }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const list = useInfiniteQuery(conversationListQuery(orgId, 'needs', undefined));
  const needs = useMemo(() => list.data?.pages.flatMap((p) => p.items), [list.data]);
  const needsRef = useRef(needs);
  useEffect(() => {
    needsRef.current = needs;
  }, [needs]);
  const [handled, setHandled] = useState(0);

  const snapshot = useCallback(() => (needsRef.current ?? []).map((c) => c.id), []);
  const record = useCallback(() => setHandled((n) => n + 1), []);

  const undo = useCallback(
    async (draftId: string, conversationId: string) => {
      try {
        const d = await unwrap(api.GET('/api/v1/orgs/{orgId}/drafts/{draftId}', { params: { path: { orgId, draftId } } }));
        const r = await cancelSend(orgId, d, 'Undone right after approving');
        await Promise.all(draftInvalidations(orgId, conversationId).map((k) => qc.invalidateQueries({ queryKey: k })));
        if (r.outcome !== 'prevented') {
          toast.show({ tone: 'accent', title: 'Sending already started' });
          return false;
        }
        setHandled((n) => Math.max(0, n - 1));
        return true;
      } catch (e) {
        toast.show({ tone: 'accent', title: 'Undo failed', body: describeError(e).body, durationMs: 10_000 });
        return false;
      }
    },
    [orgId, qc, toast],
  );

  const advance = useCallback(
    (fromId: string, snap: string[], h?: Handled) => {
      const at = snap.indexOf(fromId);
      const order = at < 0 ? snap : [...snap.slice(at + 1), ...snap.slice(0, at)];
      // Prefer what still needs you now; the snapshot covers a list that is mid refresh.
      const live = new Set((needsRef.current ?? []).map((c) => c.id));
      const next = order.find((id) => id !== fromId && (live.size === 0 || live.has(id))) ?? null;
      if (h?.kind === 'approved') {
        toast.show({
          title: `Approved, sends ${formatClock(h.sendAt)}`,
          action: {
            label: 'Undo',
            onAction: () => {
              void undo(h.draftId, fromId).then((ok) => {
                if (ok) void navigate({ to: '/app/conversations/$conversationId', params: { conversationId: fromId }, search: true });
              });
            },
          },
          durationMs: 10_000,
        });
      }
      if (next) void navigate({ to: '/app/conversations/$conversationId', params: { conversationId: next }, search: true });
      else void navigate({ to: '/app/conversations', search: (s) => ({ ...s, view: undefined, q: undefined }) });
    },
    [navigate, toast, undo],
  );

  const value = useMemo<Flow>(() => ({ needs, handled, snapshot, record, advance, undo }), [needs, handled, snapshot, record, advance, undo]);
  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
}
